/**
 * Inserts markdown formatting at the cursor position in a textarea
 * @param textarea - The textarea element
 * @param text - The current text value
 * @param type - The type of formatting to apply
 * @returns The new text value and cursor position
 */
export function insertMarkdown(
  textarea: HTMLTextAreaElement | null,
  text: string,
  type: "bold" | "italic" | "list" | "code"
): { newText: string; newCursorPos: number } {
  if (!textarea) {
    return { newText: text, newCursorPos: text.length };
  }

  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const selectedText = text.substring(start, end);
  const hasSelection = start !== end;

  let before = text.substring(0, start);
  let after = text.substring(end);
  let insertText = "";
  let cursorOffset = 0;

  switch (type) {
    case "bold":
      if (hasSelection) {
        insertText = `**${selectedText}**`;
        cursorOffset = insertText.length;
      } else {
        insertText = "**bold text**";
        cursorOffset = 2; // Position cursor between the **
      }
      break;

    case "italic":
      if (hasSelection) {
        insertText = `*${selectedText}*`;
        cursorOffset = insertText.length;
      } else {
        insertText = "*italic text*";
        cursorOffset = 1; // Position cursor between the *
      }
      break;

    case "list":
      // If at start of line or after newline, just add list marker
      const isStartOfLine = start === 0 || text[start - 1] === "\n";
      if (isStartOfLine) {
        insertText = hasSelection ? `- ${selectedText}` : "- List item";
        cursorOffset = hasSelection ? insertText.length : 2; // After "- "
      } else {
        // Add newline before list marker
        insertText = hasSelection ? `\n- ${selectedText}` : "\n- List item";
        cursorOffset = hasSelection ? insertText.length : 3; // After "\n- "
      }
      break;

    case "code":
      if (hasSelection) {
        insertText = `\`${selectedText}\``;
        cursorOffset = insertText.length;
      } else {
        insertText = "`code`";
        cursorOffset = 1; // Position cursor between the `
      }
      break;
  }

  const newText = before + insertText + after;
  const newCursorPos = start + cursorOffset;

  return { newText, newCursorPos };
}

/**
 * Handles keyboard shortcuts for markdown formatting
 * @param event - The keyboard event
 * @param textarea - The textarea element
 * @param text - The current text value
 * @returns The formatted text and cursor position, or null if no shortcut matched
 */
export function handleMarkdownShortcut(
  event: React.KeyboardEvent<HTMLTextAreaElement>,
  textarea: HTMLTextAreaElement | null,
  text: string
): { newText: string; newCursorPos: number } | null {
  // Ctrl/Cmd + B for bold
  if ((event.ctrlKey || event.metaKey) && event.key === "b") {
    event.preventDefault();
    return insertMarkdown(textarea, text, "bold");
  }

  // Ctrl/Cmd + I for italic
  if ((event.ctrlKey || event.metaKey) && event.key === "i") {
    event.preventDefault();
    return insertMarkdown(textarea, text, "italic");
  }

  // Ctrl/Cmd + E for code
  if ((event.ctrlKey || event.metaKey) && event.key === "e") {
    event.preventDefault();
    return insertMarkdown(textarea, text, "code");
  }

  return null;
}
